import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faSlidersH, faUserGear, faMicrochip } from '@fortawesome/free-solid-svg-icons';
import OTAEnabledToggle from '../OTAEnabledToggle';
import SettingSelect from '../SettingSelect';
import SettingToggle from '../SettingToggle';
import { API_BASE_URL } from '../../config/api';
import { TESTER_OPTIONS } from '../../utils/tester';
import { LOGGING_MODE_OPTIONS, DEFAULT_LOGGING_MODE } from '../../utils/loggingMode';
import { NEW_DEVICE_OTA_OPTIONS, normalizeNewDeviceOtaSetting } from '../../utils/otaMode';

const SettingsView: React.FC = () => {
  return (
    <div className="container-fluid mt-3">
      <div className="row mb-4">
        <div className="col">
          <h2 className="mb-1">
            <FontAwesomeIcon icon={faGear} className="me-2 text-secondary" />
            Settings
          </h2>
          <p className="text-muted mb-0">Global server settings and defaults for new accounts.</p>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-header bg-white border-0 pb-0">
              <div className="d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                  style={{ width: '2rem', height: '2rem' }}
                >
                  <FontAwesomeIcon icon={faSlidersH} />
                </span>
                <h5 className="mb-0">OTA</h5>
              </div>
              <div className="text-muted small">Global controls for OTA behavior.</div>
            </div>
            <div className="card-body d-flex flex-column gap-3">
              <div className="rounded-3 bg-light border p-3">
                <OTAEnabledToggle apiBaseUrl={API_BASE_URL} />
                <div className="text-muted small mt-2">
                  Master switch. When off, no device receives an OTA update — including first-connect, deploys, and Check for Update.
                </div>
              </div>
              <div className="rounded-3 bg-light border p-3">
                <SettingSelect
                  apiBaseUrl={API_BASE_URL}
                  settingKey="auto_update_new_devices"
                  title="Auto update new devices"
                  description="What happens the first time a device checks in. Off offers nothing. Opt-in tells the user an update is available so they can accept. Force applies it automatically."
                  options={NEW_DEVICE_OTA_OPTIONS}
                  defaultValue="off"
                  saveDescription="First-connect OTA mode for newly seen devices"
                  normalizeValue={normalizeNewDeviceOtaSetting}
                />
              </div>
              <div className="rounded-3 bg-light border p-3">
                <SettingToggle
                  apiBaseUrl={API_BASE_URL}
                  settingKey="manual_check_bypasses_ota_eligibility"
                  label="Manual update checks bypass OTA eligibility"
                  description="When someone taps Check for Update in the app, offer any available production update as opt-in even if the device OTA mode is None. Routine background check-ins still respect the device mode. Only affects devices on firmware v3.5.9 or newer."
                  saveDescription="Let user-initiated update checks bypass the device OTA mode"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-header bg-white border-0 pb-0">
              <div className="d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success-subtle text-success"
                  style={{ width: '2rem', height: '2rem' }}
                >
                  <FontAwesomeIcon icon={faUserGear} />
                </span>
                <h5 className="mb-0">New Account Defaults</h5>
              </div>
              <div className="text-muted small">
                Applied automatically whenever a new user account is created.
              </div>
            </div>
            <div className="card-body d-flex flex-column gap-3">
              <div className="rounded-3 bg-light border p-3">
                <SettingSelect
                  apiBaseUrl={API_BASE_URL}
                  settingKey="default_tester"
                  title="Default Testing Mode for new accounts"
                  description="Testing group assigned to newly created user accounts."
                  options={TESTER_OPTIONS.filter((option) => option.value !== 'internal')}
                  defaultValue="none"
                  saveDescription="Default testing group for new user accounts"
                />
              </div>
              <div className="rounded-3 bg-light border p-3">
                <SettingSelect
                  apiBaseUrl={API_BASE_URL}
                  settingKey="default_logging_mode"
                  title="Default Logging Mode for new accounts"
                  description="Logging mode assigned to newly created user accounts."
                  options={LOGGING_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  defaultValue={DEFAULT_LOGGING_MODE}
                  saveDescription="Default logging mode for new user accounts"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-6">
          <div className="card h-100 shadow-sm border-0">
            <div className="card-header bg-white border-0 pb-0">
              <div className="d-flex align-items-center gap-2">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-circle bg-info-subtle text-info"
                  style={{ width: '2rem', height: '2rem' }}
                >
                  <FontAwesomeIcon icon={faMicrochip} />
                </span>
                <h5 className="mb-0">Device Registration</h5>
              </div>
              <div className="text-muted small">
                Applied when a device is registered or assigned to an account.
              </div>
            </div>
            <div className="card-body d-flex flex-column gap-3">
              <div className="rounded-3 bg-light border p-3">
                <SettingToggle
                  apiBaseUrl={API_BASE_URL}
                  settingKey="auto_apply_account_tester_on_registration"
                  label="Auto convert devices to account's Testing Mode on registration"
                  description="When a device is registered to an account, set the device's deployment track to match the account's testing mode (none → prod, internal → dev, beta → beta)."
                  saveDescription="Copy account testing mode onto device on registration"
                />
              </div>
              <div className="rounded-3 bg-light border p-3">
                <SettingToggle
                  apiBaseUrl={API_BASE_URL}
                  settingKey="auto_apply_account_logging_mode_on_registration"
                  label="Auto convert devices to account's Logging Mode on registration"
                  description="When a device is registered to an account, set the device's logging mode to match the account's logging mode (and push the change to the device over MQTT)."
                  saveDescription="Copy account logging mode onto device on registration"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
